import { useMemo, useCallback } from 'react';
import _ from 'underscore';
import { IAdsCopilotProps, useConversationRestoration } from '@bingads-webui/ads-copilot-ui';
import { ILogger, useIsProdEnvironment } from '@bingads-webui-react/copilot-utils';
// eslint-disable-next-line import/no-cycle
import { LayoutMode } from './utils';
import { DataSessionManager, IPreferenceService } from './DataSessionManager';

interface IMsaDataSessionManagerFactory {
  preferenceService?: IPreferenceService;
  logger?: ILogger;
}

export const getTestSessionInfoKey = (preferenceService: IPreferenceService | undefined, windowObj = window) => {
  const isProd = useIsProdEnvironment(windowObj.location.host);
  const urlParams = new URLSearchParams(windowObj.location.search);

  const copilotSessionPreferenceName = 'copilottestsession';
  const copilotTestSessionId = urlParams.get(copilotSessionPreferenceName);

  const cid = urlParams.get('cid') || '';
  const isProdTestCID = (cid === '253624349' || cid === '254163285');

  if (copilotTestSessionId && (!isProd || isProdTestCID)) {
    const preferenceValue = preferenceService?.findByNameAtUserLevel(copilotSessionPreferenceName);
    const { Data: copilotSessionKeys } = preferenceValue || [];
    if (copilotSessionKeys && !copilotSessionKeys.includes(copilotTestSessionId)) {
      const updatedCopilotSessionKeys = [...copilotSessionKeys, copilotTestSessionId];
      if (updatedCopilotSessionKeys.length > 30) {
        updatedCopilotSessionKeys.shift(); // Remove the oldest key
      }
      preferenceService?.setAtUserLevel({
        Name: copilotSessionPreferenceName,
        Data: updatedCopilotSessionKeys,
      });
    }

    return `${copilotTestSessionId}`;
  }
  return undefined;
};

export const getSessionInfoKey = (preferenceService: IPreferenceService | undefined, windowObj = window) => {
  if (preferenceService) {
    const testSessionKey = getTestSessionInfoKey(preferenceService, windowObj);
    if (testSessionKey) return testSessionKey;
  }
  const urlParams = new URLSearchParams(windowObj.location.search);
  const cid = urlParams.get('cid') || '';
  return `ADS_COPILOT_SESSION_${cid}`;
};

// We need to provide non-hook implementation for this because there are a lot of class components in ui-next
export const msaDataSessionManagerFactory = _.memoize(({
  preferenceService,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  logger,
}: IMsaDataSessionManagerFactory = {}) => new DataSessionManager({
  domain: window.location.hostname.replace(/^.*\.?ui/, ''),
  sessionInfoKey: getSessionInfoKey(preferenceService),
  preferenceService,
  // We don't want to log the session info -> WI #5880258
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSaveSessionInfo: (info, key) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onGetSessionInfo: (info, key) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onClearSessionInfo: (info, key) => {},
}));

interface IUseMSAConversationRestorationWithCookieParams {
  botServiceConfig: IAdsCopilotProps['botServiceConfig'];
  logger: ILogger;
  mode: LayoutMode;
  onCopilotModeChange(param: { mode: LayoutMode }): void;
  onConnectionStatusChange?: IAdsCopilotProps['onConnectionStatusChange'];
  preferenceService?: IPreferenceService;
}

export const useMSAConversationRestorationWithCookie = ({
  botServiceConfig,
  logger,
  mode,
  onCopilotModeChange,
  onConnectionStatusChange,
  preferenceService,
}: IUseMSAConversationRestorationWithCookieParams) => {
  const onBeforeValidateToken = useCallback(() => {
    logger.trace('[Copilot] validating token in cookie');
  }, [logger]);

  const onTokenValidationSuccess = useCallback(() => {
    logger.trace('[Copilot] token validation success');
  }, [logger]);

  const onTokenValidationFail = useCallback((error: any) => {
    logger.trace(`[Copilot] token validation fail. Error: ${JSON.stringify(error)}`);
  }, []);

  const msaCookieSessionManager = msaDataSessionManagerFactory({ preferenceService, logger });

  const conversationRestorationOptions = useMemo(() => ({
    sessionManager: msaCookieSessionManager,
    botServiceConfig,
    logger,
    mode,
    onCopilotModeChange,
    onConnectionStatusChange,
    onBeforeValidateToken,
    onTokenValidationSuccess,
    onTokenValidationFail,
  }), [botServiceConfig, mode, msaCookieSessionManager, onBeforeValidateToken, onConnectionStatusChange, onCopilotModeChange, onTokenValidationSuccess]);
  const result = useConversationRestoration(conversationRestorationOptions);

  return result;
};
